<script setup lang="ts">
import { ref, reactive, computed, onMounted, inject, Ref } from 'vue';
import axios, { AxiosError } from 'axios';
import { useRouter } from 'vue-router';
import { env } from '@/utils';

const form = reactive({
    email: '',
    password: '',
});

const errors = ref({});
const generalError = ref('');
const processing = ref(false);
const router = useRouter()
// Dev-only shortcut logins: only ever exposed in local/development, never in
// staging/production or any other environment.
const showSkip = computed(() => ['local', 'development'].includes(env().VITE_APP_ENV))

async function submit() {
    errors.value = {};
    generalError.value = '';
    processing.value = true;

    await axios.post('/login', {
        email: form.email,
        password: form.password,
    })
    .then(() => {
        //TODO: Replace with proper call to router.push()
        // (need a way somewhere to choose redirect between participant and consultant)
        window.location.href = "/app";
    })
    .catch((error: AxiosError) => {
        processing.value = false;
        const code = error.response?.status ?? 400

        switch (code) {
            // For some reason axios returns a 422 even for invalid credentials
            case 422:
                // Validation errors
                // console.log(error.response.data)
                // errors.value = error.response.data["errors"] || {};
                // break;
            case 401:
                // Invalid credentials
                generalError.value = 'Email o codice univoco invalido.';
                break;
            default:
                generalError.value = `Ops! Qualcosa è andato storto. Riprova più tardi.`;
                break;
        }
    });
};

async function skipPart(idx: number) {
    if (!showSkip.value) {
        return
    }
    form.email = `participant${idx}@example.com`
    form.password = "password"
    submit()
}

async function skipCons(idx: number) {
    if (!showSkip.value) {
        return
    }
    form.email = `consultant${idx}@example.com`
    form.password = "password"
    submit()
}

function skipToDashboard() {
    window.location.href = "/admin"
}

const showLogout = inject<Ref<Boolean>>('show-logout')

onMounted(() => {
    showLogout.value = false
})
</script>

<template>
    <div class="col items-center h-full gap-30 justify-center">
        <h1 class="text-2xl font-bold text-blue">
            CNR AI Chat login
        </h1>
        <form @submit.prevent="submit" class="grid grid-cols-1 grid-rows-3 gap-y-3 w-[320px]">
            <div class="col">
                <input v-model="form.email" required type="email" placeholder="Email">
                <span v-if="errors['email']" class="text-error text-sm mt-1 block">
                    {{ errors["email"][0] }}
                </span>
            </div>
            <div class="col">
                <input v-model="form.password" required placeholder="Password">
                <span v-if="errors['password']" class="text-error text-sm mt-1 block">
                    {{ errors['password'][0] }}
                </span>
            </div>
            <div class="relative">
                <button class="btn-primary size-full" type="submit" :disabled="!form.email || !form.password">
                    Accedi
                </button>
                <div v-if="generalError"
                class="absolute left-0 -bottom-3 translate-y-full size-full rounded-md bg-error-light p-4">
                    <div class="text-sm text-error">
                        {{ generalError }}
                    </div>
                </div>
            </div>
        </form>
        <div v-if="showSkip" class="col gap-4">
            <template v-if="!env().VITE_FORCE_NO_CHAT">
                <p class="text-sm">
                    Skip to Consultant
                </p>
                <div class="row justify-around">
                    <button v-for="i in Array(4).keys()" class="btn-secondary min-w-7.5!" @click="skipCons(i+1)">
                        {{ i+1 }}
                    </button>
                </div>
            </template>
            <p class="text-sm">
                Skip to Participant
            </p>
            <div class="row justify-around">
                <button v-for="i in Array(4).keys()" class="btn-secondary min-w-7.5!" @click="skipPart(i+1)">
                    {{ i+1 }}
                </button>
            </div>
            <button class="btn-primary" @click="skipToDashboard">
                Dashboard
            </button>
        </div>
    </div>
</template>

<style scoped>
@reference 'tailwindcss';
@reference '#app.css';

::placeholder {
   @apply text-center;
}

input {
    @apply flex-1 border-2 text-sm h-12.5 rounded-md p-1;
}
</style>